import {
  isWorkbookValueListOperator,
  type WorkbookColumnarSheet,
  type WorkbookDiagnostic,
} from "@erp/shared-types";
import type { ResultColumn, SheetKindAdapter } from "../sheetKinds";
import { formulaTableIds } from "../tableReferences";
import { missingInput } from "../missingInput";
import { columnarLineage } from "./lineage";
import { columnarColumnReads } from "./columnReads";
import { planColumnarSheet } from "./plan";

// Result columns keep their identity while the Worksheet's columns do, so
// readers (Report conversions) can cache on them.
const resultColumnsOf = new WeakMap<
  WorkbookColumnarSheet["columns"],
  ResultColumn[]
>();

/** The row-by-row kind: input columns, Lookups, Formula columns, filters and sort. */
export const columnarSheetKind: SheetKindAdapter<WorkbookColumnarSheet> = {
  input: (sheet) => sheet.inputId,
  // Table references come last, so a Worksheet without any keeps its Fingerprint.
  reads: (sheet, tables) => [
    sheet.inputId,
    ...sheet.lookups.map((lookup) => lookup.sourceId),
    ...new Set(
      sheet.columns.flatMap((column) =>
        formulaTableIds(column.expression, tables),
      ),
    ),
  ],
  missingReads: (sheet, ids) => [
    ...missingInput(sheet.id, sheet.inputId, ids),
    ...sheet.lookups.flatMap((lookup, index): WorkbookDiagnostic[] =>
      ids.has(lookup.sourceId)
        ? []
        : [
            {
              code: "missing-lookup-source",
              message: "Lookup source does not exist.",
              location: {
                part: "lookup",
                itemId: sheet.id,
                index,
                field: "sourceId",
              },
            },
          ],
    ),
  ],
  resultColumns: (sheet) => {
    const cached = resultColumnsOf.get(sheet.columns);
    if (cached) return cached;
    const columns = sheet.columns.map(({ name, type, numberFormat }) =>
      numberFormat === undefined
        ? { name, type }
        : { name, type, numberFormat },
    );
    resultColumnsOf.set(sheet.columns, columns);
    return columns;
  },
  dataNamedColumnType: () => undefined,
  plan: (sheet, { schemas, tables, dependencies }) =>
    planColumnarSheet(sheet, schemas, tables, dependencies),
  // Hashes as it did before sheet kinds, so its Results stay Current.
  fingerprintInput: (sheet) => [
    sheet.inputId,
    [
      sheet.columns.map((column) => [
        column.name,
        column.expression,
        column.type,
      ]),
      // Only a Value list filter reads `values`; other filters hash as they always did.
      sheet.filters.map((filter) =>
        isWorkbookValueListOperator(filter.operator)
          ? [filter.column, filter.operator, filter.values ?? []]
          : [filter.column, filter.operator, filter.value],
      ),
      sheet.lookups.map((lookup) => [
        lookup.sourceId,
        lookup.inputColumn,
        lookup.matchColumn,
        lookup.valueColumn,
        lookup.outputColumn,
        lookup.missing,
      ]),
      sheet.sort.map((sort) => [sort.column, sort.direction]),
    ],
  ],
  lineage: columnarLineage,
  columnReads: columnarColumnReads,
  partName: (sheet, location) => {
    switch (location.part) {
      case "column":
        return sheet.columns[location.index]?.name;
      case "filter":
        return sheet.filters[location.index]?.column;
      case "sort":
        return sheet.sort[location.index]?.column;
      default:
        return undefined;
    }
  },
  mapFormulas: (sheet, { row }) => ({
    ...sheet,
    columns: sheet.columns.map((column) => ({
      ...column,
      expression: row(column.expression),
    })),
  }),
  presentationFormats: (sheet) =>
    new Map(sheet.columns.map((column) => [column.name, column.numberFormat])),
};
